import * as React from "react";
import { Button } from "./Button";

/**
 * What a customer sees when a screen fails to render — instead of a blank page, or React Router's
 * developer page with a stack trace and "Hey developer 👋", which is what an unexpected API field
 * produced in the account console (measured 2026-11-02: no route had an errorElement).
 *
 * It says what happened in one sentence, offers the two ways forward, and keeps the error text
 * behind "Details" for whoever is asked to look at it.
 */
export function ErrorScreen({
  error,
  homeHref,
  onRetry,
}: {
  error?: unknown;
  /** Where "Go to the start" leads — the console's own root. */
  homeHref: string;
  onRetry?: () => void;
}) {
  const message =
    error instanceof Error ? error.message : typeof error === "string" ? error : error == null ? "" : JSON.stringify(error);
  return (
    <div role="alert" className="mx-auto max-w-md px-4 py-12 text-center">
      <div className="text-[17px] font-semibold text-fg">This page could not be shown</div>
      <p className="mt-2 text-[13.5px] leading-relaxed text-muted">
        Something on it did not load as expected. Nothing was changed. Try again, or go back to the start.
      </p>
      <div className="mt-5 flex flex-wrap justify-center gap-2">
        <Button variant="accent" onClick={() => (onRetry ? onRetry() : window.location.reload())}>
          Try again
        </Button>
        <Button asChild variant="default">
          <a href={homeHref}>Go to the start</a>
        </Button>
      </div>
      {message && (
        <details className="mt-6 text-left text-[12px] text-muted">
          <summary className="cursor-pointer text-center">Details for support</summary>
          <pre className="mt-2 overflow-x-auto rounded-lg border border-line bg-bg2 p-3 font-mono text-[11.5px] whitespace-pre-wrap break-all">
            {message}
          </pre>
        </details>
      )}
    </div>
  );
}

/**
 * ErrorBoundary — catches a render failure below it and shows ErrorScreen in its place. Put one
 * around the CONTENT (not the header), so a broken page still leaves the navigation working.
 * `resetKey` clears the error when it changes — pass the current path, so moving to another page
 * leaves the failed one behind.
 */
export class ErrorBoundary extends React.Component<
  { children: React.ReactNode; homeHref: string; resetKey?: unknown },
  { error: unknown }
> {
  state: { error: unknown } = { error: null };

  static getDerivedStateFromError(error: unknown) {
    return { error };
  }

  componentDidUpdate(prev: { resetKey?: unknown }) {
    if (prev.resetKey !== this.props.resetKey && this.state.error != null) this.setState({ error: null });
  }

  componentDidCatch(error: unknown) {
    // Kept in the browser console for whoever is asked to look — never sent anywhere.
    console.error("ozpanel: a screen failed to render", error);
  }

  render() {
    if (this.state.error != null) {
      return <ErrorScreen error={this.state.error} homeHref={this.props.homeHref} onRetry={() => this.setState({ error: null })} />;
    }
    return this.props.children;
  }
}
